import { useEffect, useState } from "react";
import { Copy, Check } from "lucide-react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { subscribe, useConfig } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";
import { IMG } from "@/lib/content";

const CodeBox = ({ code, onDone }) => {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  return (
    <motion.div data-testid="discount-popup-success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", stiffness: 200 }}>
      <p className="text-xs text-white/60 mb-2">{t("popup_code_label")}</p>
      <button data-testid="discount-code-copy-button" onClick={() => { navigator.clipboard?.writeText(code); setCopied(true); }}
        className="w-full h-14 border border-dashed border-[#c9a55c] flex items-center justify-between px-5 font-display text-2xl text-gold">
        <span data-testid="discount-code-value">{code}</span>{copied ? <Check size={18} /> : <Copy size={18} />}
      </button>
      <button data-testid="discount-popup-shop-button" onClick={onDone} className="btn-gold w-full h-14 mt-4">{t("popup_start")}</button>
    </motion.div>
  );
};

export const DiscountPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const { setDiscount } = useCart();
  const { t } = useI18n();
  const { data: cfg } = useConfig();
  const pct = cfg?.discount_percent || 10;

  useEffect(() => {
    if (localStorage.getItem("ajs_popup_done")) return;
    const show = () => { if (!localStorage.getItem("ajs_popup_done")) { setOpen(true); localStorage.setItem("ajs_popup_done", "1"); } };
    const timer = setTimeout(show, 30000);
    return () => clearTimeout(timer);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const r = await subscribe(email, "popup");
      setCode(r.discount_code);
      setDiscount(r.discount_code);
    } catch {
      setError(t("invalid_email"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="discount-popup" className="bg-[#0e0d0b] border border-[#c9a55c]/30 p-0 max-w-3xl overflow-hidden grid md:grid-cols-2 gap-0">
        <div className="hidden md:block bg-white overflow-hidden">
          <motion.img src={IMG.primus} alt="Primus" className="h-full w-full object-cover" initial={{ scale: 1.25 }} animate={{ scale: 1 }} transition={{ duration: 2.5, ease: [0.22, 1, 0.36, 1] }} />
        </div>
        <div className="p-8 md:p-10 flex flex-col justify-center">
          <p className="eyebrow mb-3">{t("popup_eyebrow")}</p>
          <DialogTitle className="font-display text-4xl leading-tight mb-3 text-white">{t("popup_title_1", { pct })}<br />{t("popup_title_2")}</DialogTitle>
          <DialogDescription className="text-white/60 text-sm mb-6">{t("popup_text")}</DialogDescription>
          {code ? <CodeBox code={code} onDone={() => setOpen(false)} /> : (
            <form onSubmit={submit} className="space-y-3">
              <input data-testid="discount-popup-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("email_placeholder")}
                className="w-full h-14 bg-transparent border border-white/15 px-5 text-sm text-white focus:outline-none focus:border-[#c9a55c]" />
              {error && <p data-testid="discount-popup-error" className="text-xs text-red-400">{error}</p>}
              <button data-testid="discount-popup-submit-button" className="btn-gold btn-shine w-full h-14">{t("popup_unlock", { pct })}</button>
              <button type="button" data-testid="discount-popup-decline" onClick={() => setOpen(false)} className="w-full text-xs text-white/40 hover:text-white/70 pt-1">{t("popup_decline")}</button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
